import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Search, User } from "lucide-react";

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_sv-talent-hub/artifacts/r52eruox_D8A27E47-7BC7-497A-8381-399E44D09C54.PNG";

const links = [
  { to: "/", label: "Home" },
  { to: "/women", label: "Women" },
  { to: "/men", label: "Men" },
  { to: "/talent", label: "Talent" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <header className={`sticky top-0 z-50 transition-colors ${scrolled ? "bg-[#050506]/95 backdrop-blur-xl border-b border-[rgba(255,255,255,0.06)]" : "bg-transparent"}`}>
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-4 sm:px-8 lg:px-12 h-24">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
          <img src={LOGO} alt="SV Talent Today" className="h-14 w-14 rounded-md object-cover shadow-[0_4px_18px_rgba(0,0,0,0.5)]" />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif-hero text-[22px] text-[#F4E4BC] tracking-[0.02em]">SV</div>
            <div className="font-display-cinzel text-[12px] text-[#F4EFE4] tracking-[0.28em] leading-none">TALENT TODAY</div>
            <div className="font-mono-label text-[8.5px] text-[#C5A059] mt-1 tracking-[0.34em]">TALENT MEETS OPPORTUNITY</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-[11px] font-mono-label tracking-[0.3em] uppercase transition-colors ${isActive(l.to) ? "text-[#F4E4BC] pb-1 border-b border-[#C5A059]" : "text-[#CDC7BA] hover:text-[#F4E4BC]"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <Link to="/talent" aria-label="Search" data-testid="header-search" className="text-[#CDC7BA] hover:text-[#F4E4BC]"><Search size={18} strokeWidth={1.4} /></Link>
          <Link to={user ? (user.role === "admin" ? "/admin" : "/dashboard/talent") : "/login"} aria-label="Account" data-testid="header-account" className="text-[#CDC7BA] hover:text-[#F4E4BC]"><User size={18} strokeWidth={1.4} /></Link>
          {user ? (
            <button onClick={async () => { await logout(); nav("/"); }} className="px-6 py-3 border border-[#C5A059] rounded-none text-[11px] font-mono-label tracking-[0.3em] text-[#F4E4BC] hover:bg-[#C5A059] hover:text-[#0A0A0B] transition">LOGOUT</button>
          ) : (
            <Link to="/membership" data-testid="header-join-btn" className="px-7 py-3 border border-[#C5A059] rounded-none text-[11px] font-mono-label tracking-[0.32em] text-[#F4E4BC] hover:bg-[#C5A059] hover:text-[#0A0A0B] transition">JOIN NOW</Link>
          )}
        </div>

        <button data-testid="header-mobile-toggle" className="lg:hidden p-2 text-[#C5A059]" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[rgba(255,255,255,0.06)] bg-[#050506] px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className={`text-sm font-mono-label tracking-[0.28em] uppercase ${isActive(l.to) ? "text-[#F4E4BC]" : "text-[#CDC7BA]"}`}>{l.label}</Link>
          ))}
          <div className="flex gap-3 pt-2 border-t border-[rgba(255,255,255,0.06)]">
            {user ? (
              <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="px-6 py-3 border border-[#C5A059] text-[11px] font-mono-label tracking-[0.28em] text-[#F4E4BC]">LOGOUT</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-6 py-3 border border-[rgba(197,160,89,0.4)] text-[11px] font-mono-label tracking-[0.28em] text-[#CDC7BA]">LOGIN</Link>
                <Link to="/membership" onClick={() => setOpen(false)} className="px-6 py-3 border border-[#C5A059] text-[11px] font-mono-label tracking-[0.28em] text-[#F4E4BC]">JOIN NOW</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
